import { useEffect } from 'react'

/**
 * A tiny module-level registry for "does the current page have unsaved work,
 * and if so what would be lost". React-context-free by design (mirrors
 * `apps/console/src/services/api/robot-alerts-store.ts`'s module-scoped
 * pattern) so it can be read from plain functions — `executeClientTool.ts`
 * and a `NavLink` click handler — that sit outside any component tree.
 *
 * **What this guards, and what it does not.** This covers the NAVIGATION
 * SURFACE only: the sitewide assistant's `navigate_to` tool (already routed
 * through our code), the header `NavLink`s, and any page exit that calls
 * `confirmLeaveIfUnsaved()` itself (the Forms builder's back link and
 * Submissions button). `useBlocker` would cover every transition uniformly,
 * but it requires a data router (`useDataRouterContext`/`useDataRouterState`)
 * and `main.tsx` mounts plain `<BrowserRouter>`, which provides neither —
 * calling it would throw.
 * Migrating ~40 routes to `createBrowserRouter` to get `useBlocker` is a large
 * unrelated refactor, so this task guards the surface instead (recorded
 * decision, spec §6.2). A BARE `Link`/`navigate()` that does not call
 * `confirmLeaveIfUnsaved()` — today the shell's Logo, SettingsDrawer links and
 * sign-out, and the credit pill — bypasses this registry entirely, as does the
 * browser's own Back/Forward. This is a real, stated limit, not an oversight.
 *
 * At most ONE page registers at a time in practice (there is one route
 * mounted), but the registry does not enforce that — the last registration
 * wins, and unmounting clears only itself (see `useRegisterUnsavedWork`).
 */

type Describe = () => string | null

let current: Describe | null = null

/**
 * What would be lost by leaving the current page right now, or `null` when
 * nothing would be. Read by `executeClientTool.ts` before honouring
 * `navigate_to`, and by the header `NavLink` click guard.
 */
export function hasUnsavedWork(): string | null {
  return current ? current() : null
}

/**
 * The one interruption point for a human leaving the current page in-app: when
 * the page has registered unsaved work, ask (naming what would be lost) and
 * return whether to go ahead. Returns `true` without asking when nothing is
 * unsaved.
 *
 * Used by the header `NavLink`s and by a page's own exits (e.g. the Forms
 * builder's back link and Submissions button). A `Link` caller does
 * `if (!confirmLeaveIfUnsaved()) event.preventDefault()`; a `navigate()` caller
 * returns early on `false`. The assistant's `navigate_to` does NOT use this: it
 * reads `hasUnsavedWork()` and refuses outright, since there is no human at a
 * prompt to ask.
 */
export function confirmLeaveIfUnsaved(): boolean {
  const unsaved = hasUnsavedWork()
  if (!unsaved) return true

  // eslint-disable-next-line no-alert -- deliberate: the one interruption point for the navigation surface guard.
  return window.confirm(`You have ${unsaved}. Leave this page and discard them?`)
}

/**
 * Register `describe` as the source of truth for "is this page dirty" for as
 * long as the calling component is mounted.
 *
 * MUST unregister on unmount. A registration that outlives its page would
 * block every later navigation app-wide with a description of work that no
 * longer exists — the single failure mode this hook exists to prevent.
 */
export function useRegisterUnsavedWork(describe: Describe): void {
  useEffect(() => {
    current = describe

    return () => {
      // Only clear if we are still the registered describer — a second page
      // mounting before the first's cleanup runs must not have its
      // registration erased by the first's unmount.
      if (current === describe) current = null
    }
  }, [describe])
}

/** Test-only reset of the module state. */
export function __resetUnsavedWorkForTests(): void {
  current = null
}
